import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CaseStudy,
  CaseStudyMedia,
  CaseStudySection,
} from '../content/caseStudies';
import Lightbox, { LightboxImage } from './Lightbox';
import './CaseStudyDetail.css';

interface Props {
  study: CaseStudy;
}

/**
 * Reusable case study layout. Renders the four canonical sections
 * (Overview, Approach, Solution, Impact) plus optional metrics, prototype,
 * and per-section media (images and videos with placeholder fallback).
 *
 * Real images are clickable — opening a full-screen Lightbox.
 */
export default function CaseStudyDetail({ study }: Props) {
  const [from, to] = study.thumbnail.gradient;
  const [lightbox, setLightbox] = useState<LightboxImage | null>(null);
  const openLightbox = (img: LightboxImage) => setLightbox(img);
  const closeLightbox = () => setLightbox(null);

  return (
    <article className="case-detail">
      {/* Hero */}
      <header className="case-detail__hero container">
        <Link className="case-detail__back" to="/#case-studies">
          ← Back to all work
        </Link>
        <ul className="case-detail__tags" aria-label="Project tags">
          {study.tags.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <h1 className="case-detail__title display">{study.title}</h1>
        <p className="case-detail__summary">{study.summary}</p>
        <dl className="case-detail__meta">
          <div>
            <dt>Client</dt>
            <dd>{study.client}</dd>
          </div>
        </dl>
      </header>

      {/* Hero banner — real image if provided, gradient fallback otherwise */}
      <div
        className="case-detail__hero-image"
        role="img"
        aria-label={study.hero?.alt ?? study.thumbnail.alt}
        style={
          study.hero?.src
            ? { backgroundImage: `url(${study.hero.src})` }
            : { backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }
        }
      >
        {!study.hero?.src && (
          <span className="case-detail__hero-placeholder-label">
            {study.hero?.alt ?? 'Hero image placeholder'}
          </span>
        )}
      </div>

      {/* Body sections */}
      <div className="container case-detail__body">
        <Section section={study.overview} onOpenImage={openLightbox} />
        {(study as any).goal && (
          <Section section={(study as any).goal} onOpenImage={openLightbox} />
        )}
        {study.approach && (
          <Section section={study.approach} onOpenImage={openLightbox} />
        )}
        {study.solution && (
          <Section section={study.solution} onOpenImage={openLightbox} />
        )}

        {study.metrics && study.metrics.length > 0 && (
          <section
            className="case-detail__metrics"
            aria-labelledby="metrics-heading"
          >
            <h2 id="metrics-heading" className="case-detail__section-title">
              Outcomes
            </h2>
            <ul className="case-detail__metrics-list">
              {study.metrics.map((m) => (
                <li key={m.label} className="case-detail__metric">
                  <span className="case-detail__metric-value">{m.value}</span>
                  <span className="case-detail__metric-label">{m.label}</span>
                  {m.detail && (
                    <span className="case-detail__metric-detail">{m.detail}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {study.impact && (
          <Section section={study.impact} onOpenImage={openLightbox} />
        )}

        {study.prototype && (
          <p className="case-detail__prototype">
            <a
              className="btn btn--ghost"
              href={study.prototype.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {study.prototype.label} ↗
            </a>
          </p>
        )}
      </div>

      {/* Image lightbox — renders only when an image is open */}
      <Lightbox image={lightbox} onClose={closeLightbox} />
    </article>
  );
}

function renderInlineBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index}>{part.slice(2, -2)}</strong>
      );
    }
    return part;
  });
}

function renderBodyParagraph(text: string, key: number) {
  const trimmed = text.trim();

  if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
    return (
      <h3 key={key} className="case-detail__subtitle">
        {trimmed.slice(2, -2)}
      </h3>
    );
  }

  return <p key={key}>{renderInlineBold(text)}</p>;
}

function Section({
  section,
  onOpenImage,
}: {
  section: CaseStudySection;
  onOpenImage: (img: LightboxImage) => void;
}) {
  return (
    <section
      className="case-detail__section"
      aria-labelledby={`section-${slugify(section.heading)}`}
    >
      <h2
        id={`section-${slugify(section.heading)}`}
        className="case-detail__section-title"
      >
        {section.heading}
      </h2>
      <div className="case-detail__section-body">
        {section.paragraphs.map((p, i) => renderBodyParagraph(p, i))}

        {section.bullets && section.bullets.length > 0 && (
          <ul className="case-detail__bullets">
            {section.bullets.map((b, i) => {
              const trimmed = b.trim();
              const isBoldTitle = /^\*\*(.*?)\*\*$/.test(trimmed);
              return (
                <li
                  key={i}
                  className={
                    trimmed === ''
                      ? 'case-detail__bullet-separator'
                      : isBoldTitle
                      ? 'case-detail__bullet-heading'
                      : undefined
                  }
                >
                  {trimmed === '' ? null : renderInlineBold(b)}
                </li>
              );
            })}
          </ul>
        )}

        {section.media && section.media.length > 0 && (
          <div
            className={
              section.mediaLayout === '2col'
                ? 'case-detail__media case-detail__media--2col'
                : 'case-detail__media'
            }
          >
            {section.media.map((m, i) => (
              <Media key={i} media={m} onOpenImage={onOpenImage} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** Render a single media item — image, native video, embed, or placeholder. */
function Media({
  media,
  onOpenImage,
}: {
  media: CaseStudyMedia;
  onOpenImage: (img: LightboxImage) => void;
}) {
  // Placeholders and videos still need a defined box (no source to set
   // the height); real images can render at their intrinsic ratio.
  const fallbackAspectRatio = media.aspectRatio ?? '16 / 9';

  // No src — render a placeholder block (always uses the fallback ratio)
  if (!media.src) {
    return (
      <figure
        className="case-detail__figure case-detail__figure--placeholder"
        aria-label={media.alt}
      >
        <div
          className="case-detail__placeholder"
          style={{ aspectRatio: fallbackAspectRatio }}
        >
          <span className="case-detail__placeholder-kind">
            {media.kind === 'video' ? 'Video placeholder' : 'Image placeholder'}
          </span>
          <span className="case-detail__placeholder-label">{media.alt}</span>
        </div>
        {media.caption && <figcaption>{media.caption}</figcaption>}
      </figure>
    );
  }

  // Image — clickable, opens the lightbox.
  // By default the figure flexes to the image's intrinsic aspect ratio
  // (no cropping). If `media.aspectRatio` is explicitly set, we force the
  // ratio and cover-crop, which is occasionally useful for tall portrait
  // shots that would otherwise dominate the layout.
  if (media.kind === 'image') {
    const src = media.src;
    const forceRatio = media.aspectRatio !== undefined;
    return (
      <figure className="case-detail__figure">
        <button
          type="button"
          className="case-detail__image-button"
          onClick={() =>
            onOpenImage({ src, alt: media.alt, caption: media.caption })
          }
          aria-label={`Enlarge image: ${media.alt}`}
        >
          <img
            src={src}
            alt={media.alt}
            loading="lazy"
            style={
              forceRatio
                ? { aspectRatio: media.aspectRatio, objectFit: 'cover' }
                : undefined
            }
          />
          <span className="case-detail__image-zoom" aria-hidden="true">
            ⤢
          </span>
        </button>
        {media.caption && <figcaption>{media.caption}</figcaption>}
      </figure>
    );
  }

  // Video — three providers: native, YouTube, Vimeo
  const provider = media.provider ?? detectProvider(media.src);

  if (provider === 'youtube' || provider === 'vimeo') {
    const embedUrl =
      provider === 'youtube' ? toYouTubeEmbed(media.src) : toVimeoEmbed(media.src);
    return (
      <figure className="case-detail__figure">
        <div
          className="case-detail__video-frame"
          style={{ aspectRatio: fallbackAspectRatio }}
        >
          <iframe
            src={embedUrl}
            title={media.alt}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
        {media.caption && <figcaption>{media.caption}</figcaption>}
      </figure>
    );
  }

  // Native (mp4 / webm)
  return (
    <figure className="case-detail__figure">
      <video
        src={media.src}
        poster={media.poster}
        controls
        preload="metadata"
        playsInline
        style={{ aspectRatio: fallbackAspectRatio, width: '100%' }}
        aria-label={media.alt}
      />
      {media.caption && <figcaption>{media.caption}</figcaption>}
    </figure>
  );
}

// ---------- helpers ----------

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function detectProvider(url: string): 'native' | 'youtube' | 'vimeo' {
  if (/youtube\.com|youtu\.be/.test(url)) return 'youtube';
  if (/vimeo\.com/.test(url)) return 'vimeo';
  return 'native';
}

function toYouTubeEmbed(url: string): string {
  // Accept watch?v=ID, youtu.be/ID, embed/ID — return the embed form.
  const idMatch =
    url.match(/[?&]v=([^&]+)/) ||
    url.match(/youtu\.be\/([^?&]+)/) ||
    url.match(/embed\/([^?&]+)/);
  const id = idMatch ? idMatch[1] : '';
  return `https://www.youtube.com/embed/${id}`;
}

function toVimeoEmbed(url: string): string {
  const idMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  const id = idMatch ? idMatch[1] : '';
  return `https://player.vimeo.com/video/${id}`;
}
